import React, {useState, useEffect, useCallback, useContext, useRef} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {FaEye, FaEdit, FaTrashAlt} from 'react-icons/fa';
import {confirmAlert} from 'react-confirm-alert';

import {UserContext} from '../../UserContext';
import {useHttpClient} from '../hooks/useHttpClient';
import {useNotificacoes} from '../hooks/useNotificacoes';
import Pagination from '../pagination/Pagination';
import {useHasRole} from '../hooks/useHasRole';

import styles from './ListaEmpresas.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

function ListaEmpresas() {
    const {user} = useContext(UserContext);
    const navigate = useNavigate();
    const {get, del} = useHttpClient();
    const {exibirErro} = useNotificacoes();

    const [empresas, setEmpresas] = useState([]);
    const [filtros, setFiltros] = useState({nome: '', cnpj: '', page: 0, pageSize: 10});
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const inicializou = useRef(false);
    const podeExcluir = useHasRole(['ADMINISTRADOR']);

    // proteger rota
    useEffect(() => {
        if (!user) navigate('/login');
    }, [user, navigate]);

    // busca inicial
    useEffect(() => {
        inicializou.current = true;
        fetchEmpresas(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchEmpresas = useCallback(async (page) => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                ...(filtros.nome && {nome: filtros.nome}),
                ...(filtros.cnpj && {cnpj: filtros.cnpj}),
                idEmpresa: String(user.empresa.id),
                page: page.toString(),
                pageSize: filtros.pageSize.toString(),
            }).toString();

            const resp = await get(`/hello-sol/empresas?${params}`);
            setEmpresas(resp.content || []);
            setTotalPages(resp.totalPages ?? 0);
            setFiltros(f => ({...f, page}));
        } catch (err) {
            setError(err.message || 'Erro ao buscar empresas');
            exibirErro(err.message);
        } finally {
            setLoading(false);
        }
    }, [get, filtros, exibirErro]);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFiltros((prev) => ({...prev, [name]: value}));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        fetchEmpresas(0);
    };

    const confirmDelete = (id) => {
        confirmAlert({
            title: 'Confirmação de Exclusão',
            message: 'Tem certeza que deseja excluir esta empresa?',
            buttons: [
                {
                    label: 'Sim',
                    onClick: async () => {
                        try {
                            await del(`/hello-sol/empresas/${id}`);
                            fetchEmpresas(filtros.page);
                        } catch (err) {
                            exibirErro(err.message || 'Erro ao deletar empresa');
                        }
                    }
                },
                {label: 'Não'}
            ],
        });
    };

    const handlePageChange = (page) => {
        if (page >= 0 && page < totalPages) {
            fetchEmpresas(page);
        }
    };

    const formatCnpj = (value = '') =>
        value.replace(/^([0-9]{2})([0-9]{3})([0-9]{3})([0-9]{4})([0-9]{2}).*/, '$1.$2.$3/$4-$5');

    return (
        <div className={styles_global.container}>
            <h2>Consultar Empresa</h2>

            <form onSubmit={handleSubmit} className={styles.filterForm}>
                <div className={styles_content.formField}>
                    <label htmlFor="cnpj">CNPJ</label>
                    <input
                        id="cnpj"
                        type="text"
                        name="cnpj"
                        value={filtros.cnpj}
                        onChange={handleChange}
                        placeholder="Digite o CNPJ"
                        maxLength="14"
                    />
                </div>
                <div className={styles_content.formField}>
                    <label htmlFor="nome">Nome</label>
                    <input
                        id="nome"
                        type="text"
                        name="nome"
                        value={filtros.nome}
                        onChange={handleChange}
                        placeholder="Digite o Nome"
                        maxLength="100"
                    />
                </div>

                <div className={styles.botoesAbaixo}>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Consultando...' : 'Consultar'}
                    </button>
                    {podeExcluir && (
                        <button type="button" onClick={() => navigate('/home/cadastrar-empresa')}>
                            Nova Empresa
                        </button>
                    )}
                </div>
            </form>

            {loading && <p className={styles_global.loading}>Carregando...</p>}
            {error && <p className={styles_global.errorMessage}>{error}</p>}

            {!loading && empresas.length > 0 && (
                <>
                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                            <tr>
                                <th>CNPJ</th>
                                <th>Nome</th>
                                <th>Ações</th>
                            </tr>
                            </thead>
                            <tbody>
                            {empresas.map((empresa, idx) => (
                                <tr key={empresa.id} className={idx % 2 === 0 ? 'even-row' : 'odd-row'}>
                                    <td>{formatCnpj(empresa.cnpj)}</td>
                                    <td>{empresa.nome}</td>
                                    <td className={styles.actions}>
                                        <Link to={`/home/detalhar-empresa/${empresa.id}`} className={styles.iconButton}>
                                            <FaEye title="Visualizar" className={styles.icon}/>
                                        </Link>
                                        <Link to={`/home/cadastrar-empresa/${empresa.id}`}
                                              className={styles.iconButton}>
                                            <FaEdit title="Editar" className={styles.icon}/>
                                        </Link>
                                        {podeExcluir && (
                                            <Link
                                                to="#"
                                                onClick={() => confirmDelete(empresa.id)}
                                                className={`${styles.iconButton}`}>
                                                <FaTrashAlt title="Excluir"
                                                            className={`${styles.icon} ${styles.delete}`}/>
                                            </Link>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        currentPage={filtros.page}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </>
            )}

            {!loading && empresas.length === 0 && inicializou.current && (
                <p className={styles_global.noData}>Nenhuma empresa encontrada.</p>
            )}
        </div>
    );
}

export default ListaEmpresas;
